// الهيكل التنظيمي والمسارات والملفات الوظيفية + حسم الخلايا غير المحسومة + طلبات الاستكمال
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { useDebounced, errText } from "@/v2/lib/hooks";
import { GlassCard, Chip, Skeleton } from "@/v2/components/ui";
import { Modal, Table, Pagination, PageHead, Empty } from "@/v2/admin/ui";
import { LEVELS, LEVEL_AR, TYPE_AR, TYPE_TONE, LEVEL_TONE, useLookups } from "@/masar/lib";
import { Network, Search, ChevronLeft, Plus, CheckCircle2, AlertTriangle, Inbox } from "lucide-react";

function ProfileModal({ id, onClose, canEdit }) {
  const qc = useQueryClient();
  const { data: p, isLoading } = useQuery({ queryKey: ["masar-profile", id], queryFn: async () => (await api.get(`/masar/admin/profiles/${id}`)).data });
  const [editing, setEditing] = useState(null);
  const [level, setLevel] = useState("");
  const [note, setNote] = useState("");

  const decide = async (cid, notRequired) => {
    if (!notRequired && !level) return toast.error("اختر المستوى");
    if (note.trim().length < 3) return toast.error("اكتب سبب القرار (يُوثَّق في سجل الإدارة)");
    try {
      await api.patch(`/masar/admin/profiles/${id}/requirements/${cid}`, { approved_level: level || null, admin_note: note, not_required: notRequired });
      toast.success("حُسمت الخلية"); setEditing(null); setLevel(""); setNote("");
      qc.invalidateQueries({ queryKey: ["masar-profile", id] }); qc.invalidateQueries({ queryKey: ["masar-profiles"] });
    } catch (e) { toast.error(errText(e)); }
  };

  return (
    <Modal title="الملف الوظيفي" onClose={onClose} wide testId="profile-modal">
      {isLoading || !p ? <Skeleton className="h-64 w-full" /> : (
        <div className="space-y-4">
          <div className="v2-inset p-4">
            <div className="text-lg font-extrabold">{p.title}</div>
            <div className="flex flex-wrap gap-2 mt-2">
              {p.context.units.map((u) => <Chip key={u.id} tone="muted">{u.kind_ar}: {u.name}</Chip>)}
              {p.context.track && <Chip tone="emerald">المسار: {p.context.track.name}</Chip>}
              {p.position_key && <Chip tone="gold">{p.context.position_label}</Chip>}
              <Chip tone={p.status === "ok" ? "emerald" : "gold"}>{p.status === "ok" ? "معتمد" : `قيد المراجعة (${p.review_count})`}</Chip>
            </div>
            <div className="text-xs text-ink-2 mt-2">المصدر: {p.sheet} · الصف {p.row} · {p.source === "manual" ? `إضافة إدارية (${p.created_by})` : "مصفوفة Excel"} · الموظفون المرتبطون: {p.employees.length}</div>
          </div>
          <div className="space-y-1.5 max-h-[50vh] overflow-y-auto pe-1" data-testid="profile-requirements">
            {p.requirements.filter((r) => r.status !== "empty" || r.warning).map((r) => (
              <div key={r.competency_id} className="v2-inset p-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <Chip tone={TYPE_TONE[r.type]}>{r.type_ar}</Chip>
                  <span className="font-bold text-sm flex-1">{r.competency_name}</span>
                  {r.status === "ok" && <Chip tone={LEVEL_TONE[r.approved_level]}>{r.approved_level} · {LEVEL_AR[r.approved_level]}</Chip>}
                  {r.status === "review" && <Chip tone="red"><AlertTriangle size={11} /> غير محسومة</Chip>}
                  {r.status === "empty" && <Chip tone="muted">غير مطلوب (قرار إداري)</Chip>}
                  <span className="text-xs text-ink-2" dir="ltr">{r.cell}</span>
                </div>
                {(r.reason || r.warning || r.admin_note) && (
                  <div className="text-xs text-ink-2 mt-1 space-y-0.5">
                    {r.reason && <div>السبب: {r.reason}</div>}
                    {r.warning && <div className="text-beige-ink">{r.warning}</div>}
                    {r.admin_note && <div>قرار إداري: {r.admin_note} {r.decided_by && `— ${r.decided_by}`}</div>}
                    {r.cached && r.status === "review" && <div>القيمة المخزنة في الملف: «{r.cached}» (لا تُعتمد آليًا)</div>}
                  </div>
                )}
                {canEdit && r.status === "review" && (
                  editing === r.competency_id ? (
                    <div className="mt-2 flex flex-wrap gap-2 items-center">
                      <select value={level} onChange={(e) => setLevel(e.target.value)} className="v2-input !w-auto !py-1.5 text-sm" data-testid="req-level"><option value="">المستوى</option>{LEVELS.map((l) => <option key={l} value={l}>{l} · {LEVEL_AR[l]}</option>)}</select>
                      <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="سبب القرار" className="v2-input !w-auto flex-1 !py-1.5 text-sm" data-testid="req-note" />
                      <button onClick={() => decide(r.competency_id, false)} className="v2-btn v2-btn-sm" data-testid="req-approve">اعتماد</button>
                      <button onClick={() => decide(r.competency_id, true)} className="v2-btn-ghost v2-btn-sm" data-testid="req-not-required">غير مطلوب</button>
                    </div>
                  ) : <button onClick={() => setEditing(r.competency_id)} className="v2-btn-ghost v2-btn-sm mt-2" data-testid="req-resolve">حسم الخلية</button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </Modal>
  );
}

function NewProfileModal({ onClose }) {
  const qc = useQueryClient();
  const { data: lk } = useLookups();
  const [f, setF] = useState({ title: "", unit_id: "", track_id: "", track_name: "", position_key: "" });
  const [reqs, setReqs] = useState([]);
  const [cid, setCid] = useState(""); const [lvl, setLvl] = useState("");
  const save = async () => {
    try { await api.post("/masar/admin/profiles", { ...f, requirements: reqs }); toast.success("أُضيف الملف الوظيفي"); qc.invalidateQueries({ queryKey: ["masar-profiles"] }); onClose(); }
    catch (e) { toast.error(errText(e)); }
  };
  return (
    <Modal title="إضافة مستوى/مسمى ناقص (إضافة إدارية)" onClose={onClose} wide testId="new-profile-modal">
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2"><label className="v2-label">المسمى الوظيفي</label><input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="v2-input" data-testid="np-title" /></div>
        <div><label className="v2-label">الوحدة التنظيمية</label><select value={f.unit_id} onChange={(e) => setF({ ...f, unit_id: e.target.value })} className="v2-input" data-testid="np-unit"><option value="">—</option>{(lk?.units || []).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}</select></div>
        <div><label className="v2-label">المسار الوظيفي</label><select value={f.track_id} onChange={(e) => setF({ ...f, track_id: e.target.value })} className="v2-input" data-testid="np-track"><option value="">— جديد / بلا مسار —</option>{(lk?.tracks || []).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}</select></div>
        {!f.track_id && <div><label className="v2-label">أو اسم مسار جديد</label><input value={f.track_name} onChange={(e) => setF({ ...f, track_name: e.target.value })} className="v2-input" /></div>}
        <div><label className="v2-label">منصب إشرافي؟</label><select value={f.position_key} onChange={(e) => setF({ ...f, position_key: e.target.value })} className="v2-input"><option value="">لا</option>{(lk?.positions || []).map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}</select></div>
      </div>
      <div className="v2-inset p-3 mt-4">
        <div className="text-sm font-bold mb-2">المتطلبات (جدارة + مستوى)</div>
        <div className="flex gap-2 flex-wrap">
          <select value={cid} onChange={(e) => setCid(e.target.value)} className="v2-input flex-1 !py-1.5 text-sm" data-testid="np-comp"><option value="">الجدارة</option>{(lk?.competencies || []).map((c) => <option key={c.id} value={c.id}>{TYPE_AR[c.type]} · {c.name}</option>)}</select>
          <select value={lvl} onChange={(e) => setLvl(e.target.value)} className="v2-input !w-auto !py-1.5 text-sm"><option value="">المستوى</option>{LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}</select>
          <button onClick={() => { if (cid && lvl) { setReqs([...reqs.filter((r) => r.competency_id !== cid), { competency_id: cid, level: lvl }]); setCid(""); setLvl(""); } }} className="v2-btn v2-btn-sm"><Plus size={13} /></button>
        </div>
        <div className="flex flex-wrap gap-1.5 mt-2">{reqs.map((r) => <Chip key={r.competency_id} tone={LEVEL_TONE[r.level]}>{(lk?.competencies || []).find((c) => c.id === r.competency_id)?.name} · {r.level}</Chip>)}</div>
      </div>
      <button onClick={save} className="v2-btn w-full mt-4" data-testid="np-save">حفظ الملف الوظيفي</button>
    </Modal>
  );
}

export default function MasarStructure() {
  const qc = useQueryClient();
  const [tab, setTab] = useState("profiles");
  const [kind, setKind] = useState("sector");
  const [parent, setParent] = useState([]);
  const [search, setSearch] = useState(""); const dSearch = useDebounced(search, 400);
  const [status, setStatus] = useState(""); const [page, setPage] = useState(1);
  const [unitFilter, setUnitFilter] = useState(null);
  const [sel, setSel] = useState(null); const [creating, setCreating] = useState(false);
  const { data: access } = useQuery({ queryKey: ["masar-access"], queryFn: async () => (await api.get("/masar/access")).data });
  const canEdit = !!access?.areas?.matrix;

  const parentId = parent.length ? parent[parent.length - 1].id : "";
  const { data: units, isLoading: uLoading } = useQuery({ queryKey: ["masar-units-admin", kind, parentId], queryFn: async () => (await api.get(`/masar/admin/units?kind=${kind}&parent_id=${parentId}`)).data, enabled: tab === "units" });
  const { data: profiles, isLoading: pLoading } = useQuery({ queryKey: ["masar-profiles", dSearch, status, page, unitFilter?.id], queryFn: async () => (await api.get(`/masar/admin/profiles?search=${encodeURIComponent(dSearch)}&status=${status}&page=${page}&limit=20&unit_id=${unitFilter?.id || ""}`)).data, enabled: tab === "profiles" });
  const { data: tracks, isLoading: tLoading } = useQuery({ queryKey: ["masar-tracks-admin", dSearch], queryFn: async () => (await api.get(`/masar/admin/tracks?q=${encodeURIComponent(dSearch)}`)).data, enabled: tab === "tracks" });
  const { data: compl = [] } = useQuery({ queryKey: ["masar-completion"], queryFn: async () => (await api.get("/masar/admin/completion-requests")).data });

  const KINDS = [["sector", "القطاعات"], ["general_department", "الإدارات العامة"], ["department", "الإدارات"], ["section", "الأقسام"]];
  const resolveCompl = async (r) => {
    const note = window.prompt("ملاحظة الرد على الموظف:", "تمت إضافة مستواك الوظيفي — أعد اختيار ملفك من مسار.");
    if (note === null) return;
    try { await api.post(`/masar/admin/completion-requests/${r.id}/resolve`, { note }); toast.success("حُسم الطلب"); qc.invalidateQueries({ queryKey: ["masar-completion"] }); } catch (e) { toast.error(errText(e)); }
  };

  return (
    <div className="space-y-5" data-testid="masar-structure">
      <PageHead icon={Network} title="الهيكل والملفات الوظيفية" sub="مستخرج من مصفوفة الجدارات كما هي: قطاع → إدارة عامة → إدارة → قسم، ومسارات ومستويات لكل صف حقيقي.">
        {canEdit && <button onClick={() => setCreating(true)} className="v2-btn v2-btn-sm" data-testid="structure-add-profile"><Plus size={14} /> إضافة مستوى ناقص</button>}
      </PageHead>
      <div className="flex gap-2 flex-wrap">
        {[["profiles", "الملفات الوظيفية"], ["tracks", "المسارات"], ["units", "الوحدات التنظيمية"], ["completion", `طلبات الاستكمال (${compl.length})`]].map(([k, l]) => (
          <button key={k} onClick={() => { setTab(k); setPage(1); }} data-testid={`structure-tab-${k}`} className={`v2-btn-sm ${tab === k ? "v2-btn" : "v2-btn-ghost"}`}>{l}</button>
        ))}
      </div>

      {tab !== "completion" && tab !== "units" && (
        <div className="flex gap-2 flex-wrap items-center">
          <div className="relative flex-1 min-w-[220px]"><Search size={15} className="absolute top-1/2 -translate-y-1/2 right-3 text-ink-2" /><input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} placeholder="بحث..." className="v2-input" style={{ paddingInlineStart: "2.5rem" }} data-testid="structure-search" /></div>
          {tab === "profiles" && <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} className="v2-input !w-auto" data-testid="structure-status"><option value="">كل الحالات</option><option value="ok">معتمد</option><option value="review">قيد المراجعة</option></select>}
          {unitFilter && <Chip tone="gold">الوحدة: {unitFilter.name} <button onClick={() => setUnitFilter(null)} className="ms-1">×</button></Chip>}
        </div>
      )}

      {tab === "profiles" && (pLoading ? <Skeleton className="h-64 w-full" /> : !profiles?.rows?.length ? <Empty text="لا توجد ملفات مطابقة." /> : (
        <>
          <Table head={["المسمى", "الجهة", "المسار", "المنصب", "الحالة", "الموظفون", "المصدر", ""]} min={900} testId="profiles-table">
            {profiles.rows.map((p) => (
              <tr key={p.id} data-testid="profile-row">
                <td className="font-semibold">{p.title}</td>
                <td className="text-xs text-ink-2">{p.context.units.map((u) => u.name).slice(-2).join(" ← ")}</td>
                <td className="text-xs">{p.context.track?.name || "—"}</td>
                <td>{p.position_key ? <Chip tone="gold">{p.context.position_label}</Chip> : "—"}</td>
                <td><Chip tone={p.status === "ok" ? "emerald" : "red"}>{p.status === "ok" ? "معتمد" : `مراجعة (${p.review_count})`}</Chip></td>
                <td>{p.employees}</td>
                <td className="text-xs text-ink-2">{p.sheet} #{p.row}</td>
                <td><button onClick={() => setSel(p.id)} className="v2-btn-ghost v2-btn-sm" data-testid={`profile-open-${p.id}`}>المتطلبات</button></td>
              </tr>
            ))}
          </Table>
          <Pagination page={page} pages={profiles.pages} onChange={setPage} />
        </>
      ))}

      {tab === "tracks" && (tLoading ? <Skeleton className="h-64 w-full" /> : (
        <Table head={["المسار الوظيفي", "الجهة", "العائلة", "المستويات", ""]} min={700} testId="tracks-table">
          {(tracks || []).map((t) => (
            <tr key={t.id}><td className="font-semibold">{t.name}</td><td className="text-xs text-ink-2">{t.unit_name || "—"}</td><td className="text-xs">{t.family}{t.subfamily && ` / ${t.subfamily}`}</td><td>{t.levels}</td>
              <td><button onClick={() => { setTab("profiles"); setSearch(""); setUnitFilter(t.unit_id ? { id: t.unit_id, name: t.unit_name } : null); }} className="v2-btn-ghost v2-btn-sm">عرض المستويات</button></td></tr>
          ))}
        </Table>
      ))}

      {tab === "units" && (
        <GlassCard className="p-5">
          <div className="flex gap-2 flex-wrap mb-3">
            {KINDS.map(([k, l]) => <button key={k} onClick={() => { setKind(k); setParent([]); }} className={`v2-btn-sm ${kind === k ? "v2-btn" : "v2-btn-ghost"}`} data-testid={`units-kind-${k}`}>{l}</button>)}
          </div>
          {parent.length > 0 && <div className="text-xs text-ink-2 mb-3 flex items-center gap-1 flex-wrap">{parent.map((p) => <span key={p.id}>{p.name} <ChevronLeft size={12} className="inline" /></span>)} <button onClick={() => { setParent([]); setKind("sector"); }} className="underline">إعادة</button></div>}
          {uLoading ? <Skeleton className="h-40 w-full" /> : (
            <Table head={["الوحدة", "النوع", "ملفات وظيفية", "فروع", "صيغ بديلة", ""]} min={640} testId="units-table">
              {(units?.items || []).map((u) => (
                <tr key={u.id}><td className="font-semibold">{u.name}</td><td className="text-xs">{u.kind_ar}</td><td>{u.profiles}</td><td>{u.children}</td>
                  <td className="text-xs text-beige-ink">{(u.variants || []).join(" · ") || "—"}</td>
                  <td className="flex gap-1">
                    {u.children > 0 && <button onClick={() => { setParent([...parent, u]); setKind(KINDS[KINDS.findIndex(([k]) => k === kind) + 1]?.[0] || kind); }} className="v2-btn-ghost v2-btn-sm">الفروع</button>}
                    <button onClick={() => { setUnitFilter(u); setTab("profiles"); }} className="v2-btn-ghost v2-btn-sm">الملفات</button>
                  </td></tr>
              ))}
            </Table>
          )}
        </GlassCard>
      )}

      {tab === "completion" && (compl.length === 0 ? <Empty text="لا طلبات استكمال مفتوحة." /> : (
        <div className="space-y-2" data-testid="completion-list">
          {compl.map((r) => (
            <GlassCard key={r.id} className="p-4 flex items-center gap-3 flex-wrap">
              <Inbox size={18} className="text-beige-ink" />
              <div className="flex-1 min-w-0"><div className="font-bold text-sm">{r.user_name}</div><div className="text-xs text-ink-2">{r.text} · {String(r.at).slice(0, 10)}</div></div>
              {canEdit && <button onClick={() => resolveCompl(r)} className="v2-btn v2-btn-sm" data-testid={`completion-resolve-${r.id}`}><CheckCircle2 size={13} /> حسم</button>}
            </GlassCard>
          ))}
        </div>
      ))}

      {sel && <ProfileModal id={sel} canEdit={canEdit} onClose={() => setSel(null)} />}
      {creating && <NewProfileModal onClose={() => setCreating(false)} />}
    </div>
  );
}
